import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Mail, KeyRound, Grid3x3, ArrowRight, Monitor } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrutalButton, BrutalCard } from "@/components/Brutal";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  const dest = redirect && redirect.startsWith("/") ? redirect : "/vandaag";
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(dest); }, [user, navigate, dest]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate(dest);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="brutal rounded-xl azure-gradient text-white w-12 h-12 flex items-center justify-center">
            <Sparkles className="w-7 h-7" strokeWidth={3} />
          </div>
          <span className="font-head font-black text-4xl tracking-tight">Slackr</span>
        </div>

        <BrutalCard className="p-6 animate-pop">
          <h1 className="font-head text-2xl font-black mb-1">{mode === "login" ? "Welkom terug!" : "Account maken"}</h1>
          <p className="text-sm text-brand-ink/60 mb-5">Beheer taken zonder eindeloos te appen.</p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required
                placeholder="Je naam" className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            )}
            <div className="relative">
              <Mail className="w-5 h-5 absolute left-3 top-3.5 text-brand-ink/50" strokeWidth={2.5} />
              <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="E-mailadres" className="w-full brutal-sm rounded-xl pl-11 pr-4 py-3 font-medium outline-none" />
            </div>
            <div className="relative">
              <KeyRound className="w-5 h-5 absolute left-3 top-3.5 text-brand-ink/50" strokeWidth={2.5} />
              <input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                placeholder="Wachtwoord" className="w-full brutal-sm rounded-xl pl-11 pr-4 py-3 font-medium outline-none" />
            </div>
            <BrutalButton data-testid="submit-login-btn" type="button" size="lg" className="w-full" disabled={busy} onClick={submit}>
              {mode === "login" ? "Inloggen" : "Registreren"} <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </BrutalButton>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="h-0.5 bg-brand-ink/15 flex-1" />
            <span className="text-xs font-bold text-brand-ink/40">OF</span>
            <div className="h-0.5 bg-brand-ink/15 flex-1" />
          </div>

          <BrutalButton data-testid="google-login-btn" variant="white" className="w-full" onClick={google}>
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Inloggen met Google
          </BrutalButton>

          <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="w-full text-center text-sm font-semibold mt-4 underline decoration-2 decoration-brand-orange">
            {mode === "login" ? "Nog geen account? Registreer" : "Al een account? Inloggen"}
          </button>
        </BrutalCard>

        <button data-testid="kiosk-link" onClick={() => { localStorage.removeItem("doenies_kiosk_verified"); localStorage.removeItem("doenies_kiosk_locked"); navigate("/kiosk"); }}
          className="mt-5 mx-auto flex items-center gap-2 text-sm font-bold font-head text-brand-ink/60 hover:text-brand-orange">
          <Monitor className="w-4 h-4" strokeWidth={2.5} /> Kiosk modus (tablet)
        </button>
      </div>
    </div>
  );
}
